import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  alertTriangle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  database: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  wifi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

const apiEndpoints = [
  {
    category: 'Plants',
    endpoints: [
      { method: 'GET', endpoint: '/api/v1/plants', description: 'List all solar plants' },
      { method: 'GET', endpoint: '/api/v1/plants/{id}', description: 'Get plant details' },
      { method: 'POST', endpoint: '/api/v1/plants', description: 'Register a new plant' },
      { method: 'PUT', endpoint: '/api/v1/plants/{id}', description: 'Update plant configuration' },
      { method: 'DELETE', endpoint: '/api/v1/plants/{id}', description: 'Remove a plant' }
    ]
  },
  {
    category: 'Metrics',
    endpoints: [
      { method: 'GET', endpoint: '/api/v1/plants/{id}/metrics', description: 'Get real-time metrics' },
      { method: 'GET', endpoint: '/api/v1/plants/{id}/metrics/history', description: 'Get historical data' },
      { method: 'GET', endpoint: '/api/v1/plants/{id}/energy', description: 'Get energy production data' },
      { method: 'GET', endpoint: '/api/v1/plants/{id}/performance', description: 'Get performance ratio' }
    ]
  },
  {
    category: 'Predictions',
    endpoints: [
      { method: 'GET', endpoint: '/api/v1/predictions', description: 'Get ML predictions' },
      { method: 'GET', endpoint: '/api/v1/predictions/forecast', description: 'Get energy forecast' },
      { method: 'GET', endpoint: '/api/v1/predictions/anomalies', description: 'Detect anomalies' },
      { method: 'GET', endpoint: '/api/v1/predictions/maintenance', description: 'Predictive maintenance alerts' }
    ]
  },
  {
    category: 'Alerts',
    endpoints: [
      { method: 'GET', endpoint: '/api/v1/alerts', description: 'List all alerts' },
      { method: 'POST', endpoint: '/api/v1/alerts', description: 'Create custom alert' },
      { method: 'PUT', endpoint: '/api/v1/alerts/{id}', description: 'Update alert settings' },
      { method: 'DELETE', endpoint: '/api/v1/alerts/{id}', description: 'Delete an alert' }
    ]
  }
]

const authMethods = [
  {
    icon: Icons.key,
    title: 'API Keys',
    description: 'Simple authentication for server-to-server integrations. Generate keys from your dashboard.',
    code: 'Authorization: Bearer YOUR_API_KEY'
  },
  {
    icon: Icons.shield,
    title: 'OAuth 2.0',
    description: 'Secure authentication for applications that act on behalf of users.',
    code: 'Authorization: Bearer ACCESS_TOKEN'
  }
]

const rateLimits = [
  { tier: 'Free', requests: '100 requests/hour', burst: '10 requests/second' },
  { tier: 'Pro', requests: '10,000 requests/hour', burst: '100 requests/second' },
  { tier: 'Enterprise', requests: 'Unlimited', burst: 'Custom' }
]

const errorCodes = [
  { code: '400', name: 'Bad Request', description: 'Invalid request parameters' },
  { code: '401', name: 'Unauthorized', description: 'Missing or invalid authentication' },
  { code: '403', name: 'Forbidden', description: 'Insufficient permissions' },
  { code: '404', name: 'Not Found', description: 'Resource does not exist' },
  { code: '429', name: 'Too Many Requests', description: 'Rate limit exceeded' },
  { code: '500', name: 'Server Error', description: 'Internal server error' }
]

export default function APIReference() {
  return (
    <div className="draft-page docs-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">API Reference</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Build with the{' '}
              <GradientText gradient="energy">AI4Solar API</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              Integrate real-time solar monitoring, ML-powered predictions, and comprehensive
              analytics into your applications with our RESTful API and WebSocket streams.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div className="cta-buttons" style={{ marginTop: 'var(--space-xl)' }}>
              <GlowButton href="#endpoints" variant="solar" size="large">
                View Endpoints
              </GlowButton>
              <GlowButton href="/docs" variant="outline" size="large">
                Documentation
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Authentication */}
      <section className="about-section" id="authentication">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Authentication</h2>
            <p>Secure your API requests with industry-standard authentication</p>
          </ScrollReveal>

          <div className="comparison-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
            {authMethods.map((method, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="value-card" whileHover={{ y: -5 }}>
                  <span className="value-icon">{method.icon}</span>
                  <h3>{method.title}</h3>
                  <p>{method.description}</p>
                  <code className="api-code-block">{method.code}</code>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section className="about-section about-dark" id="endpoints">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>REST API Endpoints</h2>
            <p>Base URL: <code style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: '4px' }}>Provided upon registration</code></p>
          </ScrollReveal>

          {apiEndpoints.map((category, i) => (
            <ScrollReveal key={i} delay={i * 0.1}>
              <div className="api-category">
                <h3 className="api-category-title">{category.category}</h3>
                <div className="api-preview">
                  <div className="api-endpoints">
                    {category.endpoints.map((endpoint, j) => (
                      <div key={j} className="api-endpoint">
                        <span className={`api-method method-${endpoint.method.toLowerCase()}`}>
                          {endpoint.method}
                        </span>
                        <code className="api-path">{endpoint.endpoint}</code>
                        <span className="api-desc">{endpoint.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* WebSocket */}
      <section className="about-section" id="websocket">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Real-time WebSocket Streams</h2>
            <p>Subscribe to live data feeds for instant updates</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="api-preview">
              <div className="api-header">
                <span className="api-badge">WebSocket</span>
                <span className="api-base-url">WebSocket URL provided upon registration</span>
              </div>
              <div className="api-endpoints">
                <div className="api-endpoint">
                  <span className="api-method method-ws">SUB</span>
                  <code className="api-path">metrics.{'{plant_id}'}</code>
                  <span className="api-desc">Real-time metrics stream</span>
                </div>
                <div className="api-endpoint">
                  <span className="api-method method-ws">SUB</span>
                  <code className="api-path">alerts.{'{plant_id}'}</code>
                  <span className="api-desc">Live alert notifications</span>
                </div>
                <div className="api-endpoint">
                  <span className="api-method method-ws">SUB</span>
                  <code className="api-path">predictions.{'{plant_id}'}</code>
                  <span className="api-desc">ML prediction updates</span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="api-features" style={{ marginTop: 'var(--space-2xl)' }}>
              <div className="api-feature">
                <span className="feature-icon">{Icons.zap}</span>
                <div>
                  <h4>Sub-second Latency</h4>
                  <p>Receive updates within milliseconds of data collection</p>
                </div>
              </div>
              <div className="api-feature">
                <span className="feature-icon">{Icons.wifi}</span>
                <div>
                  <h4>Auto-reconnect</h4>
                  <p>Built-in connection recovery with exponential backoff</p>
                </div>
              </div>
              <div className="api-feature">
                <span className="feature-icon">{Icons.database}</span>
                <div>
                  <h4>Message Buffering</h4>
                  <p>Never miss data during brief disconnections</p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="about-section about-dark" id="rate-limits">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Rate Limits</h2>
            <p>Fair usage limits to ensure reliability for all users</p>
          </ScrollReveal>

          <div className="comparison-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            {rateLimits.map((limit, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="comparison-card" whileHover={{ y: -5 }}>
                  <span className="cc-label">{limit.tier}</span>
                  <span className="cc-value" style={{ fontSize: '1.5rem' }}>{limit.requests}</span>
                  <span className="cc-label">Burst: {limit.burst}</span>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Error Codes */}
      <section className="about-section" id="errors">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Error Codes</h2>
            <p>Standard HTTP status codes with detailed error messages</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="api-preview">
              <div className="api-endpoints">
                {errorCodes.map((error, i) => (
                  <div key={i} className="api-endpoint">
                    <span className={`api-method method-${parseInt(error.code) >= 500 ? 'delete' : parseInt(error.code) >= 400 ? 'put' : 'get'}`}>
                      {error.code}
                    </span>
                    <code className="api-path">{error.name}</code>
                    <span className="api-desc">{error.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Integration Options */}
      <section className="about-section about-dark" id="sdks">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Integration Options</h2>
            <p>Multiple ways to integrate with AI4Solar</p>
          </ScrollReveal>

          <div className="partners-grid">
            <ScrollReveal delay={0.1}>
              <motion.div className="partner-card" whileHover={{ scale: 1.02 }}>
                <span className="partner-name">REST API</span>
                <span className="partner-type">Full documentation above</span>
              </motion.div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <motion.div className="partner-card" whileHover={{ scale: 1.02 }}>
                <span className="partner-name">WebSocket Streams</span>
                <span className="partner-type">Real-time data feeds</span>
              </motion.div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <a href="/contact" style={{ textDecoration: 'none' }}>
                <motion.div className="partner-card" whileHover={{ scale: 1.02 }}>
                  <span className="partner-name">Custom Integration</span>
                  <span className="partner-type">Contact us for enterprise solutions</span>
                </motion.div>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Ready to Integrate?</h2>
            <p>
              Request access to the AI4Solar API and start building integrations.
              Our developer support team is ready to help with any questions.
            </p>
            <div className="cta-buttons">
              <GlowButton href="/demo" variant="solar" size="large">
                Request Demo
              </GlowButton>
              <GlowButton href="/contact" variant="outline" size="large">
                Contact Sales
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
